import { useEffect, useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

/**
 * DashboardSection — collapsible group for the owner dashboard (Aug 2026
 * declutter). Header shows title + at-a-glance subtitle; open state is
 * remembered per-section in localStorage. `defaultOpen` only applies
 * until the owner makes a manual choice (e.g. Profile & tools stays
 * open while the profile is incomplete).
 */
export const DashboardSection = ({
  id,
  title,
  subtitle,
  icon,
  action,
  defaultOpen = false,
  children,
}) => {
  const key = `tt_dash_sec_${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch { /* private mode */ }
    return defaultOpen;
  });

  // defaultOpen can settle after an async fetch (profile completeness).
  // Follow it only while the owner has never toggled manually.
  useEffect(() => {
    try {
      if (localStorage.getItem(key) === null) setOpen(defaultOpen);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  const toggle = () => {
    setOpen((v) => {
      try { localStorage.setItem(key, v ? "0" : "1"); } catch { /* noop */ }
      return !v;
    });
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-border bg-card overflow-hidden"
      data-testid={`dash-section-${id}`}
    >
      <div className="flex items-center gap-2 pr-4">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex-1 min-w-0 flex items-center gap-3 px-5 py-4 text-left hover:bg-muted/40 transition-colors"
          data-testid={`dash-section-${id}-toggle`}
        >
          {open ? (
            <CaretDown weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
          ) : (
            <CaretRight weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
          )}
          {icon}
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base tracking-tight">{title}</div>
            {subtitle && (
              <div className="text-xs text-muted-foreground truncate" data-testid={`dash-section-${id}-subtitle`}>
                {subtitle}
              </div>
            )}
          </div>
        </button>
        {action}
      </div>
      {open && <div className="px-4 pb-4 pt-1 space-y-4 sm:px-5 sm:pb-5">{children}</div>}
    </section>
  );
};

export default DashboardSection;
